import { Dispatch, SetStateAction, useContext, useEffect } from 'react'
import { TLivePrice } from '../Pages/INStockDetailPage/types'
import { newrelicCustomEvents } from '@/root/utils/common/custom/newrelic-custom-events'
import { StocksNatsConnect } from '@/components/Common/NatsProvider'

export function useGetFnOOIFromSocket(
  subject: string | undefined,
  setLivePrice: Dispatch<SetStateAction<TLivePrice | null>> | null,
) {
  const { nats: nc } = useContext(StocksNatsConnect)

  useEffect(() => {
    let sub

    async function init() {
      try {
        // Only connect if firebase_path is received from Backend
        if (subject && nc) {
          const decoder = new TextDecoder()
          sub = nc.subscribe(subject, {
            callback: (err, msg) => {
              const decodedData = JSON.parse(decoder.decode(msg.data))
              if (setLivePrice) {
                setLivePrice({
                  price:
                    typeof decodedData?.open_interest === 'number'
                      ? Number(decodedData?.open_interest.toFixed(2))
                      : null,
                  percent_change: decodedData?.oi_change,
                  color: decodedData?.live_price_color,
                  last_updated: decodedData?.last_updated,
                })
              }
            },
          })
        }
      } catch (err: unknown) {
        if (
          err !== null &&
          typeof err === 'object' &&
          'code' in err &&
          (err as { code?: string }).code === 'CONNECTION_CLOSED'
        ) {
          return
        }

        void newrelicCustomEvents({
          customErrString: typeof err === 'object' ? JSON.stringify(err) : err,
          customErrorType: 'socket-error',
        })
        // Do nothing
        // Socket reconnects if fails, incase it throws an error, no need to display any UI error messages for now, prices will remain static
        console.info('Socket connection failed!', err)
      }
    }
    void init()
    return () => {
      if (sub) {
        void sub.unsubscribe()
      }
    }
  }, [subject, setLivePrice, nc])
}

export default function useGetINStockLivePriceFromSocket(
  subject: string | undefined,
  setLivePrice: Dispatch<SetStateAction<TLivePrice | null>> | null,
) {
  const { nats: nc } = useContext(StocksNatsConnect)

  useEffect(() => {
    let sub

    async function init() {
      try {
        // Only connect if firebase_path is received from Backend
        if (subject && nc) {
          const decoder = new TextDecoder()
          sub = nc.subscribe(subject, {
            callback: (err, msg) => {
              const decodedData = JSON.parse(decoder.decode(msg.data))

              if (setLivePrice) {
                setLivePrice({
                  price:
                    typeof decodedData?.live_price === 'number'
                      ? Number(decodedData?.live_price.toFixed(2))
                      : null,
                  percent_change: decodedData?.perc_change_float,
                  color: decodedData?.live_price_color,
                  absolute_change:
                    typeof decodedData?.abs_change === 'number'
                      ? decodedData?.abs_change
                      : null,
                  last_updated: decodedData?.last_updated,
                })
              }
            },
          })
        }
      } catch (err: unknown) {
        if (
          err !== null &&
          typeof err === 'object' &&
          'code' in err &&
          (err as { code?: string }).code === 'CONNECTION_CLOSED'
        ) {
          return
        }

        void newrelicCustomEvents({
          customErrString: typeof err === 'object' ? JSON.stringify(err) : err,
          customErrorType: 'socket-error',
        })
        // Do nothing
        // Socket reconnects if fails, incase it throws an error, no need to display any UI error messages for now, prices will remain static
        console.info('Socket connection failed', err)
      }
    }
    void init()
    return () => {
      if (sub) {
        void sub.unsubscribe()
      }
    }
  }, [subject, setLivePrice, nc])
}

export function useGetINStockLivePriceFromSocketWithKey(
  subject: string | undefined,
  setLivePrice: Dispatch<
    SetStateAction<{ [x: string]: TLivePrice | null } | null>
  > | null,
) {
  const { nats: nc } = useContext(StocksNatsConnect)

  useEffect(() => {
    let sub

    async function init() {
      try {
        // Only connect if firebase_path is received from Backend
        if (subject && nc) {
          const decoder = new TextDecoder()
          sub = nc.subscribe(subject, {
            callback: (err, msg) => {
              const decodedData = JSON.parse(decoder.decode(msg.data))

              if (setLivePrice) {
                const priceData = {
                  [subject]: {
                    price:
                      typeof decodedData?.live_price === 'number'
                        ? Number(decodedData?.live_price.toFixed(2))
                        : null,
                    percent_change: decodedData?.perc_change_float,
                    color: decodedData?.live_price_color,
                    absolute_change:
                      typeof decodedData?.abs_change === 'number'
                        ? decodedData?.abs_change
                        : null,
                    last_updated: decodedData?.last_updated,
                  },
                }
                setLivePrice(priceData)
              }
            },
          })
        }
      } catch (err: unknown) {
        if (
          err !== null &&
          typeof err === 'object' &&
          'code' in err &&
          (err as { code?: string }).code === 'CONNECTION_CLOSED'
        ) {
          return
        }

        void newrelicCustomEvents({
          customErrString: typeof err === 'object' ? JSON.stringify(err) : err,
          customErrorType: 'socket-error',
        })
        // Do nothing
        // Socket reconnects if fails, incase it throws an error, no need to display any UI error messages for now, prices will remain static
        console.info('Socket connection failed', err)
      }
    }
    void init()
    return () => {
      if (sub) {
        void sub.unsubscribe()
      }
    }
  }, [subject, setLivePrice, nc])
}
